import type { ComponentType, ReactNode } from "react";
import { Input } from "@racoon/ui";
import { BrandIcMinus, BrandIcPlus } from "@racoon/icons";
import { dropdownErrorClass, rowLabelClass } from "./order-form-styles";

/** A row of the order form: the label beside its control, named by `htmlFor`. */
export const OrderFormRow = ({
  label,
  htmlFor,
  children,
}: {
  label: ReactNode;
  htmlFor: string;
  children: ReactNode;
}) => (
  <div className="flex w-full items-center gap-4">
    <label htmlFor={htmlFor} className={rowLabelClass} data-testid="order-form-row-label">
      {label}
    </label>
    <div className="relative min-w-0 flex-1">{children}</div>
  </div>
);

/** What a picker's trigger shows: its icon and the picked value, or the placeholder. */
export const TriggerValue = ({
  icon: Icon,
  value,
  placeholder,
}: {
  icon?: ComponentType<{ className?: string }>;
  value?: string;
  placeholder: string;
}) => (
  <span className="flex min-w-0 flex-1 items-center gap-2" title={value}>
    {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-brand-blue" />}
    <span className={`truncate !text-sm ${value ? "" : "text-text-subtle"}`}>
      {value || placeholder}
    </span>
  </span>
);

/** A validation message under a row. */
export const FieldError = ({ testId, children }: { testId?: string; children: ReactNode }) => (
  <p className="w-full text-xs font-medium text-text-error" data-testid={testId}>
    {children}
  </p>
);

/** − [n] + stepper over a whole number held as typed; never steps below 1. */
export const CountStepper = ({
  id,
  testId,
  value,
  onChange,
  invalid = false,
  disabled = false,
}: {
  id: string;
  testId: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
}) => {
  const current = Number.parseInt(value, 10);
  const step = (delta: number) =>
    onChange(String(Math.max(1, (Number.isFinite(current) ? current : 1) + delta)));
  return (
    <div
      className={`flex flex-shrink-0 items-center gap-1 ${disabled ? "pointer-events-none opacity-50" : ""}`}
    >
      <BrandIcMinus
        data-testid={`${testId}-decrease`}
        className="text-brand-blue h-8 w-8 cursor-pointer"
        onClick={() => step(-1)}
      />
      <Input
        id={id}
        data-testid={testId}
        className={`!p-2 max-h-8 !w-[3rem] text-center ${invalid ? dropdownErrorClass : ""}`}
        inputMode="numeric"
        aria-invalid={invalid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      <BrandIcPlus
        data-testid={`${testId}-increase`}
        className="text-brand-blue h-8 w-8 cursor-pointer"
        onClick={() => step(1)}
      />
    </div>
  );
};
